import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Residue } from "@/types/residue/residue.types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table/index";
import { TableHeader } from "@/components/ui/table-header/index";
import { Plus } from "lucide-react";
import { ResidueForm } from "./residues/ResidueForm";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog/index";
import { useResidueTable } from "@/hooks/residues/useResidueTable";
import { useResidueColumns } from "./residues/columns";
import { useConfirmationDialog } from "@/hooks/residues/useConfirmationDialog";

export const Residues = () => {
  const { t } = useLanguage();
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  const [formOpen, setFormOpen] = useState(false);
  const [selectedResidue, setSelectedResidue] = useState<Residue | null>(null);

  const {
    filteredResidues,
    loading,
    refetch,
    searchTerm,
    setSearchTerm,
    nameFilters,
    setNameFilters,
    clearFilters,
    nameOptions,
  } = useResidueTable();

  const {
    isOpen: confirmDialogOpen,
    confirmAction,
    isLoading: isConfirmActionLoading,
    openDialog,
    closeDialog,
    handleConfirm,
  } = useConfirmationDialog(refetch);

  const handleAddResidue = () => {
    setSelectedResidue(null);
    setFormOpen(true);
  };

  const handleEditResidue = (residue: Residue) => {
    setSelectedResidue(residue);
    setFormOpen(true);
  };

  const handleDeleteAction = (residue: Residue) => {
    openDialog({
      type: "delete",
      residueId: residue.id,
      residueName: residue.name,
    });
  };

  const columns = useResidueColumns(t, handleEditResidue, handleDeleteAction);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filters = [
    {
      label: t("residues.filters.name"),
      options: nameOptions,
      selectedValues: nameFilters,
      onChange: setNameFilters,
      showSearch: true,
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-lg font-semibold">{t("residues.title")}</h2>
        {!isAssimagraUser && (
          <Button
            onClick={handleAddResidue}
            className="bg-teal-600 text-white hover:bg-teal-700 self-start sm:self-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("residues.addButton")}
          </Button>
        )}
      </div>

      <TableHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t("residues.searchPlaceholder")}
        filters={filters}
        onClearAll={clearFilters}
      />

      <div className="overflow-hidden">
        <DataTable columns={columns} data={filteredResidues} pageSize={10} className="w-full" minWidth="auto" />
      </div>

      <ResidueForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        residueToEdit={selectedResidue}
        onSuccess={refetch}
      />

      {confirmAction && (
        <ConfirmationDialog
          isOpen={confirmDialogOpen}
          onClose={closeDialog}
          onConfirm={handleConfirm}
          title={t("residues.confirmDelete.title")}
          description={t("residues.confirmDelete.description")}
          confirmText={
            isConfirmActionLoading ? t("residues.confirmDelete.actionLoading") : t("residues.confirmDelete.action")
          }
          cancelText={t("residues.cancel")}
          variant="destructive"
          isLoading={isConfirmActionLoading}
        />
      )}
    </div>
  );
};
